import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { SendWindowSummary } from './SendWindowSummary'
import { SessionCaptureCard } from './SessionCaptureCard'
import { Verdict } from './verdict'

type Health = Schema<'AccountHealthResponse'>
type Account = Schema<'AccountHealthOut'>
type Allowance = Schema<'AllowanceOut'>
type Factor = Schema<'RiskFactorOut'>

/**
 * Account health and ban risk — S-B31b, the panel `design/screens/outreach.html`
 * calls *the screen that prevents a ban*.
 *
 * **The inputs are the panel and the score is the footnote**, which is the
 * design's own instruction rather than a layout preference: *"ban risk is shown
 * as the inputs that produce it, not as an opaque score. A tenant who can see
 * 'you're at 18 of 20 posts today' understands the pause; one who sees only
 * 'risk: medium' does not."* So the two meters come first, the factors are
 * listed in full underneath, and the number sits beside them rather than
 * replacing them.
 *
 * **Nothing is computed here.** The caps, the percentages, the badge text and
 * the badge's colour all arrive from `/api/v1/outreach/accounts`. That is not
 * ceremony: the caps are `app/linkedin/actions.py`'s integers and the whole
 * claim of this screen is that it counts what the rate limiter counts, so a copy
 * of `20` in TypeScript would be a second definition of the cap waiting to
 * disagree with the first.
 *
 * **Its own screen, `/outreach/health`** (parity P-HLTH-01, 2026-09-13):
 * `outreach.html#health` draws it under its own h1 in two columns — the
 * LinkedIn account card on the left; the ban-risk card, the checkpoint alert,
 * the session capture card and the send window on the right. It sat on the
 * dashboard as a section from S-B31b until the parity fix moved it here; the
 * dashboard's LinkedIn row and the packs screen link to it.
 */
export function AccountHealthPage(): JSX.Element {
  const health = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/accounts', { signal })),
    [],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <h1 className="ar-h1">Account health</h1>

      {health.state.status === 'loading' ? (
        <Loading label="Loading your connected accounts" lines={3} />
      ) : null}

      {health.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={health.state.failure}
            title="Could not load account health"
            onRetry={health.refetch}
          />
        </div>
      ) : null}

      {health.state.status === 'ready' ? <Ready data={health.state.data} /> : null}
    </div>
  )
}

function Ready({ data }: { data: Health }): JSX.Element {
  if (data.empty) {
    return (
      <div className="ar-card">
        <Empty
          icon="in"
          title="No LinkedIn account connected"
          body="Lead discovery runs on your own LinkedIn session, so there is nothing to watch until an account is connected. Once it is, this shows the day's allowances and anything that raises the risk of a restriction."
          action={
            <Link className="ar-btn ar-btn--primary" to="/settings/connections">
              Connect LinkedIn
            </Link>
          }
        />
      </div>
    )
  }
  // The mockup draws one account; a tenant with several gets one card each on
  // the left and the shared right-hand stack once.
  return (
    <div className="ar-grid ar-grid--2">
      <div className="ar-stack">
        {data.accounts.map((account) => (
          <AccountCard
            key={account.account_key || account.label || 'default'}
            account={account}
            checkedAt={data.checked_at}
          />
        ))}
      </div>
      <div className="ar-stack">
        {data.accounts.map((account) => (
          <BanRiskCard key={account.account_key || account.label || 'risk'} account={account} />
        ))}
        {/*
          `design/screens/outreach.html:285`, and it was the one piece of that panel
          missing from this one (§I V7).

          It earns its place by answering the question the meters raise but do not
          address: a tenant reading "18 of 20 posts today" is being told how close
          the edge is, and nothing on the screen said what happens if it is crossed.
          The worry it settles — *does a LinkedIn problem stop my SEO too?* — is
          about the blast radius of a pause, not about the pause.

          Every clause is checked against the code rather than copied from the
          mockup, because a reassurance that is not true is worse than none:

            - *pause immediately* — `app/linkedin/limits.decide` refuses a paused
              account and `app/db/extension.bury_queued_searches` empties the queue
              behind it;
            - *email you* — `app/notifications/events.py` defines "LinkedIn
              paused / challenge" and marks it immediate, so
              `app/notifications/service.py` sends it at the moment it happens
              rather than in the next morning's digest;
            - *never retry* — `app/linkedin/pause.py` exists to guarantee there is
              no code path that un-pauses, only one a person presses;
            - *everything else keeps running* — the pause is a LinkedIn connection
              status, and `limits.decide` is the only thing that reads it.
        */}
        <div className="ar-alert ar-alert--warning" role="note">
          {/* OUTR-49. `⚠` rather than `!`, which is the mockup's glyph and the one
              `Cannibalization.tsx:133` already uses for a warning alert; `!` was
              the only place in the product spelling it that way. */}
          <span className="ar-alert__icon" aria-hidden="true">
            ⚠
          </span>
          <div className="ar-alert__body">
            <strong>If LinkedIn shows a checkpoint</strong>
            We pause this account immediately, email you, and never retry. Everything else — email,
            WhatsApp, and all SEO work — keeps running.
          </div>
        </div>
        <SessionCaptureCard />
        <SendWindowSummary />
      </div>
    </div>
  )
}

function AccountCard({ account, checkedAt }: { account: Account; checkedAt: string }): JSX.Element {
  return (
    <article className="ar-card">
      <div className="ar-card__header">
        {/* *LinkedIn — <name>*, the mockup's title shape (P-HLTH-03). The
            mockup prints the profile handle; the API carries the display name
            and no handle, so the name is what follows the dash. */}
        <h3>LinkedIn — {account.label ?? 'account'}</h3>
        <span className="ar-card__spacer" />
        {/* OUTR-50. The mark comes from the tone rather than from the text, so
            the API keeps sending words and this keeps deciding how to draw
            them — see `verdict.tsx`. */}
        <span title={account.badge.detail}>
          <Verdict tone={account.badge.tone}>{account.badge.text}</Verdict>
        </span>
      </div>
      <div className="ar-card__body ar-stack">
        {/* The body opens on the meters (P-HLTH-05): the badge's detail
            sentence is the badge's `title`, and the reasons behind the score
            are on the ban-risk card beside this one. The one exception is a
            paused account — a state the mockup does not draw — whose detail
            names the manual recovery, which a tooltip would hide. */}
        {account.badge.tone === 'danger' ? (
          <p className="ar-muted app-health__badge-detail">{account.badge.detail}</p>
        ) : null}
        {account.allowances.map((allowance) => (
          <Meter key={allowance.action} allowance={allowance} />
        ))}

        <hr className="app-health__rule" />

        <Facts account={account} checkedAt={checkedAt} />
      </div>
    </article>
  )
}

/**
 * The ban-risk card — `outreach.html#health`'s right-hand column opener: the
 * word, then one sentence. The score and the factors that produced it stay
 * beneath (OUTR-10, OUTR-51), because the design's own rule is that ban risk is
 * shown as the inputs that produce it.
 */
function BanRiskCard({ account }: { account: Account }): JSX.Element {
  return (
    <article className="ar-card">
      <div className="ar-card__header">
        <h3>Ban-risk score</h3>
        {account.label ? (
          <>
            <span className="ar-card__spacer" />
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
              {account.label}
            </span>
          </>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack">
        <BanRisk account={account} />
        <Reasons factors={account.factors} stopped={account.stopped} />
      </div>
    </article>
  )
}

/**
 * The account-facts table — OUTR-9.
 *
 * **What the mockup asks for and what this product has are not the same five
 * rows, and three of the differences are decisions rather than gaps.**
 * `design/screens/outreach.html:263-271` lists *Account age · Connections ·
 * Challenges seen · Proxy · Withdrawn invites*. Taking them in order:
 *
 *   - **Account age** — AugRank does not know when a LinkedIn profile was
 *     created, and could only learn it by reading the profile, which the
 *     extension protocol refuses to do by design (`app/extension/__init__.py`:
 *     *"it reads one cookie, never your messages, your feed, or anyone's
 *     profile"*). What it does know is when the account was connected **here**,
 *     which is also the age the score actually reads — `risk._newness_factor`
 *     scores `connections.connected_at`, not a LinkedIn birthday. So the row is
 *     *Connected*, and it is the honest form of the same question.
 *   - **Connections** — the profile's connection count, same refusal, and
 *     nothing in the risk model reads it.
 *   - **Challenges seen** — real, and the one row that ports unchanged.
 *   - **Proxy** — **there is no proxy.** `PROGRESS ARCH1` (2026-08-01) dropped
 *     the rented residential IP and the Fargate browser fleet outright; search
 *     runs in the tenant's own browser from their own address. A row reading
 *     *"Residential, Hyderabad · ✓ Sticky"* would describe an architecture that
 *     was deleted, and the mockup predates the decision.
 *   - **Withdrawn invites** — AugRank sends no LinkedIn connection invites.
 *     `app/outreach/posts.py` records the omission as deliberate and
 *     `app/linkedin/publish.py` is the whole of what the OAuth scope allows, so
 *     there is no invite to withdraw and no rate to report.
 *
 * **The third row is not in the mockup and is the reason the table is worth
 * drawing at all.** *Connection* states what `connections.status` says, and the
 * argument is `DeliverabilityPanel`'s about its senders: the meters being green
 * is necessary and not sufficient — an account sitting at `pending` has spent
 * nothing today for a reason that has nothing to do with its allowance.
 */
function Facts({ account, checkedAt }: { account: Account; checkedAt: string }): JSX.Element {
  // The factors that fired, by code. `assess` drops every zero-point factor
  // before it leaves the server, so presence *is* "this is counting against the
  // account" — which is what lets each row carry the server's own verdict
  // instead of a second copy of the rule that produced it.
  const firing = new Set(account.factors.map((factor) => factor.code))
  return (
    <table className="ar-table app-health__facts">
      <caption className="ar-visually-hidden">
        What this account&rsquo;s ban-risk score is made of
      </caption>
      <tbody>
        <tr>
          <td>Connected</td>
          <td className="ar-num">
            {account.connected_at === null ? 'Never' : age(account.connected_at, checkedAt)}
          </td>
          <td className="ar-num">
            {account.connected_at === null ? (
              <Verdict tone="neutral">Not yet</Verdict>
            ) : firing.has('new_account') ? (
              <Verdict tone="warning">Recently connected</Verdict>
            ) : (
              <Verdict tone="success">Established</Verdict>
            )}
          </td>
        </tr>
        <tr>
          <td>Challenges seen</td>
          <td className="ar-num">
            {account.challenges_recent}
            {account.last_challenge_at === null
              ? ''
              : ` · last ${formatDate(account.last_challenge_at)}`}
          </td>
          <td className="ar-num">
            {/* `firing`, not `challenges_recent > 0`. The two agree today —
                `_challenge_factor` scores any count above zero — but the count
                is the *reading* and the factor is the *verdict*, and writing the
                threshold out here is the second copy the comment above says this
                row does not make. A window or a floor changed in `risk.py` would
                otherwise leave this badge asserting the old rule. */}
            {firing.has('challenges') ? (
              <Verdict tone="warning">Recent</Verdict>
            ) : (
              <Verdict tone="success">Clean</Verdict>
            )}
          </td>
        </tr>
        <tr>
          <td>Connection</td>
          <td className="ar-num">{STATUS[account.status] ?? account.status}</td>
          <td className="ar-num">
            <Verdict tone={STATUS_TONE[account.status] ?? 'neutral'}>
              {account.status === 'connected' ? 'Live' : 'Not sending'}
            </Verdict>
          </td>
        </tr>
      </tbody>
    </table>
  )
}

/**
 * `connections.status` as something a tenant reads, and how to colour it.
 *
 * The six members of `CONNECTION_STATUSES`. Both maps fall back rather than
 * rendering a raw enum value or an empty class — the same argument
 * `DeliverabilityPanel.tone` makes: a status added in a later release reaches
 * this screen before this file knows about it, and an uncoloured badge beside the
 * server's own word is the honest way to draw one.
 */
const STATUS: Record<string, string> = {
  connected: 'Connected',
  pending: 'Finishing setup',
  disconnected: 'Disconnected',
  no_access: 'Access withdrawn',
  challenge: 'Checkpoint',
  failed: 'Failed',
}

const STATUS_TONE: Record<string, string> = {
  connected: 'success',
  pending: 'neutral',
  disconnected: 'neutral',
  no_access: 'warning',
  challenge: 'danger',
  failed: 'danger',
}

/**
 * How long ago, coarsely — *4 years*, *7 months*, *6 days*.
 *
 * `relativeTime` stops at days, which is right for a session list and wrong here:
 * *"1,619 days ago"* is a number nobody reads as *"this account is established"*.
 *
 * **Measured against the server's own reading time, not the browser's clock.**
 * `checked_at` is when the panel's figures were taken, and a laptop whose clock is
 * a day out must not be able to move a row from *Established* to *Recently
 * connected* — especially when the badge beside it comes from the server, which
 * used its own clock. It is the same rule `formatDuration` follows: two instants
 * from the same source.
 */
function age(fromIso: string, toIso: string): string {
  const from = new Date(fromIso).getTime()
  const to = new Date(toIso).getTime()
  if (Number.isNaN(from) || Number.isNaN(to)) return formatDate(fromIso)
  const days = Math.floor((to - from) / 86_400_000)
  // A future `connected_at` is not defensive padding: `connections.connected_at`
  // defaults to the database's clock and `checked_at` comes from the
  // application's, so the two can disagree across a correction or a restored
  // snapshot. `risk._newness_factor` guards the same case for the same reason,
  // and prints the same nothing rather than "-9 days".
  if (days <= 0) return 'Today'
  if (days < 60) return `${String(days)} day${days === 1 ? '' : 's'}`
  // The upper boundary is in days rather than in months, and that is the whole
  // of it: rounding to months first and switching at 24 of them put 720 to 729
  // days into the years branch at `floor(days / 365) === 1`, so an account that
  // had just read *23 months* went to **"1 years"** — wrong in its plural and
  // smaller than the figure it replaced. Two whole years is 730 days, so
  // switching there makes the years branch unreachable below 2 and the plural
  // correct by construction.
  if (days < 730) return `${String(Math.floor(days / 30))} months`
  return `${String(Math.floor(days / 365))} years`
}

/**
 * The ban-risk verdict — OUTR-10 and OUTR-51.
 *
 * **The word is the headline and the number is the footnote**, which inverts what
 * was here and is the design's instruction rather than a layout taste: a tenant
 * reading *48 / 100* has to be told what 48 means, and there is nowhere on the
 * screen that tells them. *Low* needs no scale. The number stays because two
 * cards side by side have to be comparable and because a score that moved from 12
 * to 40 without crossing a band is a thing worth being able to see.
 *
 * **The band is the server's verdict, not a threshold applied here.**
 * `risk.assess` decides `low` / `elevated` / `challenged` / `banned` / `unknown`
 * and it is not a pure function of the score — a challenged account is challenged
 * whatever it scores, which is the module's most emphatic paragraph. Re-deriving
 * a band from `risk_score` in TypeScript would quietly disagree with it in exactly
 * the case that matters most.
 *
 * **The sentence is the mockup's, corrected.** It reads *"account age, action
 * rate, challenge history, and proxy stability"*; two of those four are not what
 * this product measures — see `Facts` — and the claim it ends on, *no hidden
 * inputs*, is only worth making if the list is true. So it names the five factor
 * codes `risk._factors` actually returns.
 */
function BanRisk({ account }: { account: Account }): JSX.Element {
  return (
    <div className="app-health__risk">
      {/* No `aria-describedby` tying the value back to its label. It would have
          to build an id out of `account_key`, which is a free-form 200-character
          string from the provider (`""` for a tenant's single account) and so is
          not safe as an id fragment — a space in it silently breaks the
          space-separated token list. It would also buy nothing: the label, the
          word, the number and the sentence are already read in that order. */}
      {/* No label line: the card's own heading is *Ban-risk score* now. */}
      <div className={`ar-display app-health__verdict app-health__verdict--${account.risk}`}>
        {BAND[account.risk] ?? account.risk}
      </div>
      <div className="ar-mono app-health__score">{account.risk_score} / 100</div>
      <p className="ar-muted app-health__risk-note">
        Based on how long this account has been connected, how much of today&rsquo;s allowance it
        has used, how many recent actions failed, any checkpoints LinkedIn has shown, and any
        refusal on record — all of it listed here. No hidden inputs.
      </p>
    </div>
  )
}

/** `Risk` as the word the card leads with. */
const BAND: Record<string, string> = {
  low: 'Low',
  elevated: 'Elevated',
  challenged: 'Paused',
  banned: 'Restricted',
  unknown: 'Unknown',
}

/**
 * One allowance, as a meter and a fraction.
 *
 * `role="meter"` rather than `progressbar`: this is a measurement of a bounded
 * quantity that can go down as well as up, which is what a meter is for, and a
 * screen reader announcing "18 of 20 posts today" is the whole point of the
 * panel reaching somebody who cannot see the bar.
 */
function Meter({ allowance }: { allowance: Allowance }): JSX.Element {
  const caption = CAPTION[allowance.action] ?? ENFORCED
  return (
    <div>
      <div className="ar-row app-health__meter-head">
        <span>{allowance.label}</span>
        <span className="ar-card__spacer" />
        <span className="ar-mono">
          {allowance.used} / {allowance.cap}
        </span>
      </div>
      <div
        className={`ar-meter ${allowance.near_limit ? 'ar-meter--over' : ''}`}
        role="meter"
        aria-label={`${allowance.label}: ${String(allowance.used)} of ${String(allowance.cap)}`}
        aria-valuenow={allowance.used}
        aria-valuemin={0}
        aria-valuemax={allowance.cap}
      >
        <div className="ar-meter__fill" style={{ width: `${String(allowance.percent)}%` }} />
      </div>
      <div className="ar-subtle app-health__meter-note">{caption}</div>
    </div>
  )
}

/** The sentence the caps are enforced by, for a meter with nothing else to say. */
const ENFORCED = 'Enforced in code, not by judgement. The allowance releases at 00:00 IST.'

/**
 * The line under each meter — OUTR-11.
 *
 * **One caption per meter, not one under the pair.** The panel had a single
 * summary sentence after all the cards, which lost the mockup's second caption
 * entirely: *"A search is one keyword run against LinkedIn post results."* That
 * sentence is a definition, and it is the answer to the question a tenant has
 * looking at *31 / 50* — is a search a keyword, a page, or a profile? Without it
 * the meter is a number against a unit nobody has been told.
 *
 * **Keyed on `action` rather than on `label`.** `label` is copy and may be
 * rewritten; `action` is `LinkedInAction`, the same two members the limiter
 * counts. `ENFORCED` is the fallback so a third action added to that enum arrives
 * here with the true half of the sentence rather than with nothing.
 */
const CAPTION: Record<string, string> = {
  post: ENFORCED,
  // The definition alone, which is what `outreach.html:259-260` puts here. The
  // enforcement sentence is **not** repeated: the two meters are adjacent in one
  // card and the note under the first covers the pair, where saying it twice per
  // card — four times for a tenant with two accounts — reads as boilerplate and
  // buries the one sentence on this card that is a definition rather than a
  // reassurance.
  search: 'A search is one keyword run against LinkedIn post results.',
}

/**
 * Every factor behind the score, in words.
 *
 * **An empty list renders a sentence rather than nothing.** A card showing a
 * score of zero and no explanation reads as a panel that failed to load; *"No
 * risk factors on record"* reads as an account that is fine, and only one of
 * those is what a tenant should take away from a quiet week.
 */
function Reasons({ factors, stopped }: { factors: Factor[]; stopped: boolean }): JSX.Element {
  if (factors.length === 0) {
    return (
      <p className="ar-subtle app-health__reasons">
        No risk factors on record — nothing about this account's recent activity stands out.
      </p>
    )
  }
  return (
    <ul className="app-health__reasons">
      {factors.map((factor) => (
        <li key={factor.code}>
          <span className={stopped ? 'app-health__reason--stopped' : undefined}>
            {factor.detail}
          </span>{' '}
          <span className="ar-subtle">+{factor.points}</span>
        </li>
      ))}
    </ul>
  )
}
